import React, { forwardRef, useImperativeHandle } from "react";
import * as XLSX from "xlsx-js-style";
import { PrintRecord, UserRole } from '../../Booklet/components/Report/IReport';

interface ExcelDownloaderProps {
  data: PrintRecord[];
  fileName?: string;
  currentUser: any;
}

export interface ExcelDownloaderHandle {
  exportExcel: () => void;
}

const ExcelDownloader = forwardRef<ExcelDownloaderHandle, ExcelDownloaderProps>(
  ({ data, fileName = "PrintHistory.xlsx", currentUser }, ref) => {

    // Function to get status colors based on print status
    const getStatusColors = (status: string) => {
      const statusLower = status?.toLowerCase().replace(/ /g, '_') || 'not_yet_started';

      switch (statusLower) {
        case 'not_yet_started':
        case 'not_requested':
          return { bg: 'FEF3C7', text: '92400E', border: 'F59E0B' }; // amber
        case 'in_progress':
          return { bg: 'DBEAFE', text: '1E40AF', border: '3B82F6' }; // blue
        case 'completed':
          return { bg: 'D1FAE5', text: '065F46', border: '10B981' }; // green
        case 'failed':
          return { bg: 'FEE2E2', text: '991B1B', border: 'EF4444' }; // red
        case 'on_hold':
        case 'pending':
          return { bg: 'FEF3C7', text: '92400E', border: 'F59E0B' }; // amber
        default:
          return { bg: 'F3F4F6', text: '374151', border: '9CA3AF' }; // gray
      }
    };

    const exportExcel = () => {
      if (!data || data.length === 0) {
        console.warn("No data to export");
        return;
      }

      // Prepare rows based on user role
      const excelRows = data.map((item) => {
        const baseRow = {
          "DATE CREATED": new Date(item.datePrinted).toLocaleDateString('en-AU', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          }) + ' ' + new Date(item.datePrinted).toLocaleTimeString('en-AU', {
            hour: '2-digit',
            minute: '2-digit'
          }),
          "CENTRE": item.centreName,
          "CLASS DETAILS": `${item.className}\n${item.dayOfWeek}`,
        };

        if (currentUser.role === UserRole.TUTOR_ADMIN || currentUser.role === 'TUTOR_ADMIN' || currentUser.role === 'ADMIN') {
          return {
            ...baseRow,
            "TUTOR": item.tutorName,
            "BOOKLET (CLICK FOR DETAILS)": item.bookletName,
            "DRIVE PATH": item.bookletDrivePath || "",
            "TOPIC": item.topic,
            "COUNT": item.printCount,
            "STATUS": item.status || "Not Yet Started",
          };
        } else {
          return {
            ...baseRow,
            "BOOKLET (CLICK FOR DETAILS)": item.bookletName,
            "DRIVE PATH": item.bookletDrivePath || "",
            "TOPIC": item.topic,
            "COUNT": item.printCount,
            "STATUS": item.status || "Not Yet Started",
          };
        }
      });

      const ws = XLSX.utils.json_to_sheet(excelRows);

      // Column widths
      const isAdmin = currentUser.role === UserRole.TUTOR_ADMIN || currentUser.role === 'TUTOR_ADMIN' || currentUser.role === 'ADMIN';
      ws["!cols"] = isAdmin
        ? [
            { wpx: 140 }, // Date Created
            { wpx: 120 }, // Centre
            { wpx: 160 }, // Class Details
            { wpx: 120 }, // Tutor
            { wpx: 220 }, // Booklet
            { wpx: 280 }, // Drive Path (wider for links)
            { wpx: 90 },  // Topic
            { wpx: 70 },  // Count
            { wpx: 130 }, // Status
          ]
        : [
            { wpx: 140 },
            { wpx: 120 },
            { wpx: 160 },
            { wpx: 220 },
            { wpx: 280 },
            { wpx: 90 },
            { wpx: 70 },
            { wpx: 130 },
          ];


// PROFESSIONAL DARK GREY HEADER – This is the one you want
const headerStyle = {
  font: { bold: true, sz: 12, color: { rgb: "64748B" }, name: "Arial " },
  fill: { fgColor: { rgb: "f2f2f2" } },      
  alignment: { vertical: "center", horizontal: "center", wrapText: true },
  border: {
    top:    { style: "thin", color: { rgb: "94A3B8" } },
    bottom: { style: "thin", color: { rgb: "94A3B8" } },
    left:   { style: "thin", color: { rgb: "94A3B8" } },
    right:  { style: "thin", color: { rgb: "94A3B8" } }
  }
};

      // Cell styles
      const defaultCellStyle = {
        font: { sz: 12, color: { rgb: "1E293B" }, name: "Arial" },
        alignment: { vertical: "center", horizontal: "left", wrapText: true },
        fill: { fgColor: { rgb: "FFFFFF" } },
      };

      const centreStyle = { ...defaultCellStyle, font: { ...defaultCellStyle.font, bold: true, color: { rgb: "0284c7" } } };
      const bookletStyle = { ...defaultCellStyle, font: { ...defaultCellStyle.font, bold: true, color: { rgb: "0284c7" } } };
      const countStyle = { ...defaultCellStyle, font: { ...defaultCellStyle.font, bold: true }, alignment: { horizontal: "center" } };

      const range = XLSX.utils.decode_range(ws["!ref"]!);

      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          const cell = ws[cellAddress];
          if (!cell) continue;

          const rowBg = R % 2 === 0 ? "FFFFFF" : "F8FAFC"; // zebra striping

          if (R === 0) {
            cell.s = headerStyle;
          } else {
            // DATA ROWS
            const headerKey = Object.keys(excelRows[0])[C];

            switch (headerKey) {
              case "CENTRE":
                cell.s = { ...centreStyle, fill: { fgColor: { rgb: rowBg } } };
                break;

              case "BOOKLET (CLICK FOR DETAILS)":
                cell.s = { ...bookletStyle, fill: { fgColor: { rgb: rowBg } } };
                break;

              case "DRIVE PATH":
                const rawPath = (cell.v as string) || "";
                const trimmedPath = rawPath.trim();

                if (trimmedPath) {
                  let url = "";
                  if (trimmedPath.startsWith("http")) {
                    url = trimmedPath;
                  } else if (trimmedPath.includes("/folders/") || trimmedPath.includes("/file/d/")) {
                    url = trimmedPath;
                  } else {
                    url = `https://drive.google.com/drive/folders/${trimmedPath}`;
                  }

                  // Set hyperlink
                  cell.l = { Target: url, Tooltip: "Click to open in Google Drive" };

                  cell.s = {
                    font: {
                      sz: 10,
                      color: { rgb: "1D4ED8" }, // Bright blue
                      underline: true,
                      name: "Courier New",
                    },
                    alignment: { vertical: "center", horizontal: "left", wrapText: true },
                    fill: { fgColor: { rgb: rowBg } },
                    border: {
                      top: { style: "thin", color: { rgb: "E2E8F0" } },
                      bottom: { style: "thin", color: { rgb: "E2E8F0" } },
                      left: { style: "thin", color: { rgb: "E2E8F0" } },
                      right: { style: "thin", color: { rgb: "E2E8F0" } },
                    },
                  };
                } else {
                  cell.v = "-";
                  cell.s = {
                    font: { sz: 10, color: { rgb: "94A3B8" }, name: "Courier New" },
                    alignment: { horizontal: "center" },
                    fill: { fgColor: { rgb: rowBg } },
                  };
                }
                break;

              case "TOPIC":
                cell.s = {
                  ...defaultCellStyle,
                  alignment: { horizontal: "center" },
                  fill: { fgColor: { rgb: "F1F5F9" } }, // light gray bg
                };
                break;

              case "COUNT":
                cell.s = { ...countStyle, fill: { fgColor: { rgb: rowBg } } };
                break;

              case "STATUS":
                const colors = getStatusColors(cell.v as string);
                cell.s = {
                  font: { sz: 11, bold: true, color: { rgb: colors.text }, name: "Arial" },
                  alignment: { vertical: "center", horizontal: "center" },
                  fill: { fgColor: { rgb: colors.bg } },
                  // border: {
                  //   top: { style: "thin", color: { rgb: colors.border } },
                  //   bottom: { style: "thin", color: { rgb: colors.border } },
                  //   left: { style: "thin", color: { rgb: colors.border } },
                  //   right: { style: "thin", color: { rgb: colors.border } },
                  // },
                };
                break;

              default:
                cell.s = { ...defaultCellStyle, fill: { fgColor: { rgb: rowBg } } };
            }
          }
        }
      }

      // Row heights
      ws["!rows"] = [{ hpx: 40 }, ...Array(data.length).fill({ hpx: 50 })];

      // Create workbook and download
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Print History");
      XLSX.writeFile(wb, fileName, { cellStyles: true });

    };

    useImperativeHandle(ref, () => ({
      exportExcel,
    }));

    return null;
  }
);

ExcelDownloader.displayName = "ExcelDownloader";
export default ExcelDownloader;